<template>
  <div class="fts-list-view" :class="{ 'list-view--detail-view': mode == 'detail' }" ref="ftsListView"
    :data-list-view-load="loading ? 'loading' : 'loaded'" @scroll="onScroll">
    <!-- header -->
    <div class="list-header" :class="{ scrolled: scroll }" ref="listHeader">
      <div class="list-nav">
        <div class="list-nav-l">
          <template v-if="module">
            <fts-dropdown-select append-to-body placement="bottom-start" prevent-overflow-alt-axis
              prevent-overflow-tether-off
              content-class="dropdown-menu dropdown-menu--sticky list-nav-title-content list-nav-title-dropdown-menu"
              :options="moduleListingOptions" class="list-nav-title-select" @selected="evt => toListView(evt)"
              @hide="listingKeyword = null" v-if="list.id && module.menus && module.menus.length">
              <div slot="toggle" class="list-nav-title">
                <fts-label-group :item="module" emoji :emoji-size="16" class="list-nav-module" />

                <div class="list-nav-module-tag" v-if="module.isProfile">
                  <font-awesome-icon :icon="['fas', 'user']" />
                </div>

                <fts-label-group :item-id="list.id" emoji :emoji-size="16" :list="module.menus"
                  class="list-nav-listing" />
              </div>

              <div slot="menu-header" class="dropdown-filter">
                <div class="fts-form-control-filter">
                  <input class="fts-form-control" v-model="listingKeyword" placeholder="Search listing" />
                </div>
              </div>

              <div slot="option" slot-scope="{ select, option }" class="dropdown-option" @click="select(option)">
                <fts-label-group :item="option" emoji :emoji-size="16" />
              </div>
            </fts-dropdown-select>

            <div class="list-nav-title" v-else>
              <fts-label-group :item="module" emoji :emoji-size="16" class="list-nav-module" />

              <div class="list-nav-module-tag" v-if="module.isProfile">
                <font-awesome-icon :icon="['fas', 'user']" />
              </div>
            </div>
          </template>
        </div>
        <div class="list-nav-r">
          <div class="list-nav-actions" v-if="listViewLoaded">
            <!-- <fts-basket :selection="basketSelection" class="list-nav-action" /> -->
            <!-- edit mode (alpha): in-place form/canvas editing from the detail view. Internal
                 admins only, so demo mode hides it like every other alpha surface, and no customer
                 admin meets the designer until it ships. -->
            <template v-if="mode === 'detail' && !builderMode && isAdminUser && isInternal">
              <fts-spinner :size="16" show v-if="designModeLoading" />
              <fts-tippy v-else>
                <font-awesome-icon :icon="['fas', 'pen-ruler']" class="list-nav-action list-nav-action-fa"
                  @click="toggleDesignMode(moduleId)"
                  :class="[designMode ? designModeState && designModeState.error ? 'fts-text-danger' : 'fts-text-primary' : '']" />
                <template #content>
                  <div class="tippy-hint fts-text-left">
                    <span class="tippy-hint-highlight">Click</span> to turn {{ designMode ? 'off' : 'on' }} edit mode
                    <fts-release-badge alpha class="fts-align-top" />
                  </div>
                  <div v-if="designModeState && designModeState.success">
                    All changes saved
                    <template v-if="designModeState.updatedOn">on
                      {{ moment(designModeState.updatedOn).format('llll') }}</template>
                  </div>
                  <div v-else-if="designModeState && designModeState.error">
                    Something went wrong. Failed to save design changes
                  </div>
                </template>
              </fts-tippy>
            </template>

            <fts-tippy content="Workflow" append-trigger-class="list-nav-action"
              v-if="isCapableOf(SYSTEM_CAPABILITY.Workflow)">
              <i class="fts-icon-automation-condition" data-name="flow-diagram" @click="workflow"></i>
            </fts-tippy>

            <fts-tippy content="Style">
              <fts-dropdown-select append-to-body placement="bottom-start" prevent-overflow-alt-axis
                prevent-overflow-tether-off append-toggle-class="list-nav-action">
                <i slot="toggle" class="fts-icon-cog"></i>
                <template #menu="{ select }">
                  <div class="dropdown-option" @click="select(); setListViewMode('list', { moduleId, listingId });">
                    <div class="dropdown-option-icon">
                      <i class="fts-icon-list-table"></i>
                    </div>
                    <div>Switch to list view</div>
                  </div>

                  <div class="dropdown-option" @click="select(); setListViewMode('card', { moduleId, listingId });">
                    <div class="dropdown-option-icon">
                      <i class="fts-icon-list-card"></i>
                    </div>
                    <div>Switch to card view</div>
                  </div>

                  <div class="dropdown-option" @click="select(); setListViewMode('calendar', { moduleId, listingId });">
                    <div class="dropdown-option-icon">
                      <i class="fts-icon-calendar"></i>
                    </div>
                    <div>Switch to calendar view</div>
                  </div>

                  <div class="dropdown-option" @click="select(); setListViewMode('detail', { moduleId, listingId });">
                    <div class="dropdown-option-icon">
                      <i class="fts-icon-list-kanban"></i>
                    </div>
                    <div>Switch to detail view</div>
                  </div>

                  <div class="dropdown-option" @click="select(); openUserPreferenceModal();" v-if="!isIndependentView">
                    <div class="dropdown-option-icon">
                      <i class="fts-icon-cog"></i>
                    </div>
                    <div>User preference</div>
                  </div>
                </template>
              </fts-dropdown-select>
            </fts-tippy>
          </div>
        </div>
      </div>
      <!-- actions -->
      <div class="list-actions" v-if="!isIndependentView">
        <div class="lv-actions">
          <!-- create -->
          <fts-btn-hold tag="a" :href="createActivityUrl" color="primary" text-transform-init box-shadow-none
            class="fts-font-normal" @clickEvent="createEntry" @longPressEvent="createEntry(true)"
            data-v-step="tour-create-entry" v-if="isCreatable">
            <font-awesome-icon :icon="['fas', 'plus']" />New
          </fts-btn-hold>
          <!-- activity -->
          <fts-btn-hold outline :color="activity.color ? activity.color : 'primary'" :emoji="activity.emoji"
            text-transform-init class="btn-activity" v-for="activity in activityList" :key="activity.id" @clickEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected)
              )
              " @longPressEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected),
                true
              )
              ">
            <span class="btn-activity-label">{{ activity.name }}
              <fts-test-badge v-if="activity.development" :stage="activity.development"></fts-test-badge>
            </span>
          </fts-btn-hold>
          <!-- document -->
          <fts-btn outline color="ternary-green" text-transform-init class="btn-activity" v-for="action in documentList"
            :key="action.id" @click="
              executeDocument(
                action,
                resultSet.list.filter((x) => x.selected)
              )
              ">
            <font-awesome-icon :icon="['far', 'file-pdf']" class="btn-activity-icon fts-file-pdf"
              v-if="action.typeId === 'pdf'" />
            <font-awesome-icon :icon="['far', 'file-alt']" class="btn-activity-icon fts-file-excel"
              v-else-if="action.typeId === 'spreadsheet'" />
            <font-awesome-icon :icon="['far', 'file-code']" class="btn-activity-icon fts-file-html"
              v-else-if="action.typeId === 'html'" />
            <font-awesome-icon :icon="['far', 'file']" class="btn-activity-icon fts-file-default" v-else />
            <span class="btn-activity-label">{{ action.name }}
              <fts-test-badge v-if="action.development" :stage="action.development"></fts-test-badge>
            </span>
          </fts-btn>
          <!-- custom -->
          <fts-btn outline :color="action.color ? action.color : 'ternary-blue'" :emoji="action.emoji"
            text-transform-init class="btn-activity" v-for="action in actionList" :key="action.id" @click="
              executeAction(
                action,
                resultSet.list.filter((x) => x.selected)
              )
              ">
            <span :class="{ 'btn-activity-label': !action.color }">{{
              action.name
            }}</span>
          </fts-btn>
          <!-- standard -->
          <fts-btn-hold outline text-transform-init class="btn-activity"
            :color="activity.id === 'delete' ? 'danger' : 'primary'" v-for="activity in bulkActivities"
            :key="activity.id" @clickEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected)
              )
              " @longPressEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected),
                true
              )
              ">
            <i :class="activity.icon"></i> {{ activity.name }}
          </fts-btn-hold>
        </div>
      </div>
    </div>
    <!-- sample records notice (guided onboarding P2): only on the module a starter built, until cleared -->
    <fts-sample-records-banner v-if="listViewLoaded" :module-id="moduleId" />
    <!-- detail view -->
    <fts-detail-view :modules="workspaceModules" :states="states" :users="users" :module-id="moduleId"
      :listing-id="listingId" :design-mode="designMode" class="list-view-mx"
      v-if="listViewLoaded && mode == 'detail'" />
    <!-- calendar view -->
    <fts-calendar-view :workspace-id="workspaceId" :workspace-beta="workspaceBeta" :workspace-context="workspaceContext"
      :modules="workspaceModules" :states="states" :users="users" :module-id="moduleId" :listing-id="listingId"
      class="list-view-mx" @executeActivity="
        executeActivity($event.activity, $event.items, $event.fullView)
        " v-else-if="listViewLoaded && mode == 'calendar'" />

    <template v-else-if="listViewLoaded">
      <div class="list-view-content-header">
        <div class="list-view-content-header-row fts-flex-wrap-reverse">
          <!-- query (advanced filter) — shown when the advanced-filter toggle is on;
               otherwise end users get the simpler wildcard search instead -->
          <fts-query-designer :modules="workspaceModules" :states="states" :information-source="filterColumns"
            :filter="filters" :mode="'view'" v-if="isCapableOf(SYSTEM_CAPABILITY.Query) && advancedFilter" />
          <!-- control -->
          <div class="list-view-control">
            <!-- wildcard search (when the advanced-filter toggle is off; otherwise the
                 advanced filter / query designer drives the result set) -->
            <div class="list-view-search" v-if="!advancedFilter">
              <input
                class="fts-form-control list-view-search-input"
                placeholder="Search"
                v-model="searchTerm"
                @input="onSearchInput"
              />
              <span
                class="list-view-search-action list-view-search-action--clear"
                v-if="searchTerm"
                @click="clearSearch"
              >
                <font-awesome-icon :icon="['fas', 'times']" fixed-width />
              </span>
              <span
                class="list-view-search-action list-view-search-action--search"
                v-else
              >
                <font-awesome-icon :icon="['fas', 'search']" fixed-width />
              </span>
            </div>
            <!-- sort — append-to-body: .list-view-content-header is a stacking context
                 (position: sticky + z-index: 1), so an in-place panel is trapped at that
                 level however high its own z-index, and the frozen header row above it
                 swallows the clicks. On <body> the panel is at $z-index-popover instead. -->
            <fts-sort :modules="workspaceModules" :fields="columns" v-model="sort"
              append-to-body
              :total-items-exceed="isTotalItemsExceed" @closed="retrieveItems"
              v-if="isCapableOf(SYSTEM_CAPABILITY.Sort)" />
            <!-- advanced filter toggle -->
            <fts-tippy :content="advancedFilter ? 'Switch to search' : 'Advanced filter'"
              class="list-view-control-icon" :class="{ 'list-view-control-icon--active': advancedFilter }"
              v-if="isCapableOf(SYSTEM_CAPABILITY.Query)">
              <font-awesome-icon :icon="['fas', 'filter']" fixed-width @click="toggleAdvancedFilter" />
            </fts-tippy>
            <!-- refresh -->
            <fts-tippy content="Refresh list" class="list-view-control-icon"
              :class="{ 'list-view-control-icon--loading': loading }">
              <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="loading" @click="!loading && retrieveItems()" />
            </fts-tippy>
            <!-- import -->
            <fts-tippy content="Import data" class="list-view-control-icon"
              v-if="isCapableOf(SYSTEM_CAPABILITY.Write) && moduleImport">
              <font-awesome-icon :icon="['fas', 'file-upload']" fixed-width @click="importData" />
            </fts-tippy>
            <!-- export -->
            <fts-tippy 
              :content="exportLoading ? 'Exporting' : 'Export data'" 
              class="list-view-control-icon" 
              :class="{ 'list-view-control-icon--loading': exportLoading }" 
              v-if="isExportRight"
            >
              <font-awesome-icon 
                :icon="['fas', 'file-download']" 
                fixed-width 
                @click="!exportLoading && exportData()" 
              />
              <!-- temp for everything listing -->
              <fts-release-badge alpha class="fts-align-top" v-if="listingId === 'everything' && isInternal" />
            </fts-tippy>
            <!-- dashboard widget -->
            <fts-tippy content="Analytics" class="list-view-control-icon" v-if="isDashboardWidgetRight">
              <font-awesome-icon :icon="['fas', 'chart-column']" fixed-width @click="openWidgetDesignPreviewModal" />
              <!-- temp for everything listing -->
              <fts-release-badge alpha class="fts-align-top" v-if="listingId === 'everything' && isInternal" />
            </fts-tippy>
            <!-- calendar -->
            <fts-tippy content="Add my calendar" class="list-view-control-icon">
              <font-awesome-icon :icon="['far', 'calendar-plus']" fixed-width @click="openMyCalendarFormModal" />
            </fts-tippy>
            <!-- bulk -->
            <fts-tippy :content="`${isBulk ? 'Turn off' : 'Turn on'} bulk actions`" class="list-view-control-icon"
              :class="{ 'list-view-control-icon--active': isBulk }" v-if="mode === 'list'">
              <font-awesome-icon :icon="['fas', 'tasks']" fixed-width @click="setBulk" />
            </fts-tippy>
          </div>
        </div>
      </div>
      <template v-if="!listingError">
        <!-- table view -->
        <div class="list-view-mx" v-if="mode == 'list'">
          <fts-table-view class="list-view-table" :vector-id="moduleId" :list-id="listingId" :states="states" :users="users"
            :standard-actions="header.standards" :read-only-source="Boolean(header.readOnlySource)"
            :columns="getColumns" :list="formData" :bulk="isBulk" collaboration
            :search-term="appliedSearchTerm"
            attachments-row editable @selectionChanged="updateBasket" @activityExecuted="executeActivity(
              $event.activity,
              $event.items,
              $event.fullView
            )" @refresh="retrieveItems" />
        </div>
        <!-- card view -->
        <fts-card-view :vector-id="moduleId" :list-id="listingId" :states="states" :users="users"
          :standard-actions="header.standards" :read-only-source="Boolean(header.readOnlySource)"
          :activity-list="header.activities" :action-list="header.customs"
          :document-list="header.documents" :columns="getColumns" :list="formData" class="list-view-mx"
          :search-term="appliedSearchTerm"
          @activityExecuted="
            executeActivity($event.activity, $event.items, $event.fullView)
            " @actionExecuted="executeAction($event.action, $event.items)"
          @documentExecuted="executeDocument($event.action, $event.items)" @refresh="retrieveItems"
          v-if="mode == 'card'" />
        <!-- footer control -->
        <template v-if="!loading && formData.length === 0">
          <div class="list-view-footer-control list-view-footer-control--addable list-view-mx" @click="createEntry()"
            v-if="isCreatable">
            No results. Click to add new.
          </div>
          <div class="list-view-footer-control list-view-mx" v-else>
            No results.
          </div>
        </template>
        <!-- pagination -->
        <fts-pagination 
          class="list-view-pagination list-view-mx" 
          :pagination="isCapableOf(SYSTEM_CAPABILITY.Pagination)" 
          :total-items="totalItems"
          :total-items-limit="totalItemslimit"
          :page="page"
          v-model="pageSize"
          @pageChanged="pageChanged"
          @page-size-changed="setPageSizePreference"
          v-if="isCapableOf(SYSTEM_CAPABILITY.Pagination) || isCapableOf(SYSTEM_CAPABILITY.NextPage)" 
        />
      </template>
    </template>
    <!-- skeleton: initial list-mode load — mirrors the table columns so the page feels like it
         is populating rather than blocked. Other modes / refresh-over-existing keep the spinner. -->
    <div
      class="list-view-skeleton list-view-mx"
      v-if="loading && mode === 'list' && !listViewLoaded"
      aria-hidden="true"
    >
      <div class="lv-skeleton-row" v-for="n in 9" :key="n">
        <span class="lv-skeleton-bar lv-skeleton-bar--id"></span>
        <span class="lv-skeleton-bar lv-skeleton-bar--state"></span>
        <span class="lv-skeleton-bar lv-skeleton-bar--date"></span>
        <span class="lv-skeleton-avatar"></span>
        <span class="lv-skeleton-bar lv-skeleton-bar--text"></span>
      </div>
    </div>
    <!-- spinner -->
    <fts-spinner full-screen boundary="parent" v-if="loading && !(mode === 'list' && !listViewLoaded)" />
    <!-- AutoCount Full's lock (402 connector_full_required): in place of the red error -->
    <fts-connector-lock-card :lock="connectorLock" class="list-view-mx" v-if="connectorLock" />
    <!-- feedback indicate -->
    <fts-feedback-indicate ref="ftsFeedbackIndicate" />
    <fts-feedback-indicate ref="ftsExportFeedbackIndicate" modal />
    <!-- import -->
    <fts-import :moduleId="moduleId" :moduleImport="moduleImport" @importDone="retrieveItems" ref="ftsImport"
      v-if="moduleImport" />
    <!-- workflow -->
    <fts-workflow-diagram ref="ftsWorkflowDiagram" />
    <!-- document modal -->
    <fts-document-modal ref="documentModal" />
    <!-- document form modal -->
    <fts-document-form-modal ref="ftsDocumentFormModal" />
    <!-- action page modal -->
    <fts-action-page-modal ref="ftsActionPageModal" />
    <!-- list view preference modal -->
    <fts-list-view-preference-modal ref="ftsListViewPreferenceModal" />
    <!-- my calendar form modal -->
    <fts-my-calendar-form-modal :workspace-id="workspaceId" :module-id="moduleId" :list-id="listingId"
      :options="dateTimeColumns" ref="ftsMyCalendarFormModal" />

    <fts-widget-design-preview-modal :modules="workspaceModules" :states="states" :users="users"
      ref="ftsWidgetDesignPreviewModal" />
  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import moment from "moment";
import changesets from "diff-json";

import { store as canvasDesignStore } from "../store/canvasDesignStore";
import { classificationStore } from "../store/classificationStore";
import { clientStore } from "../store/clientStore";
import { authorizationStore } from "../store/authorizationStore";
import { activityPortalId } from "../portals/activityPortal";

import commons from "../commons/commons";
import queryLogic from "../commons/queryLogic";
import listing from "../commons/listing";
import { SYSTEM_CAPABILITY, isCapableOf, Listing_Max_Total_Entries, Export_Direct_Download_Threshold } from "../commons/flags";
import { buildExportPayload, exportQueryScope } from "../commons/exportPayload";
import { entryStateId } from "../commons/entryState";
import LocalStorageService from '../store/LocalStorageService';

import FTSTableView from "./views/FTSTableView";
import FTSCardView from "./views/FTSCardView";
import FTSCalendarView from "./views/FTSCalendarView";
import FTSDetailView from "./views/FTSDetailView";
import FTSSampleRecordsBanner from "./FTSSampleRecordsBanner";
import FTSQueryDesigner from "../design/FTSQueryDesigner";
import FTSPagination from "./FTSPagination";
import FTSTippy from "./FTSTippy";
import FTSSpinner from "./FTSSpinner";
import FTSForm from "./FTSForm";
import FTSToggleSwitch from "./FTSToggleSwitch";
import FTSBtnHold from "./FTSBtnHold";
import FTSBtn from "./FTSBtn";
import FTSSort from "./FTSSort";
import FTSImport from "./FTSImport";
import FTSBasket from "./FTSBasket";
import FTSWorkflowDiagram from "./FTSWorkflowDiagram";
import FTSFeedbackIndicate from "./FTSFeedbackIndicate";
import FTSConnectorLockCard from "./FTSConnectorLockCard";
import { connectorLockFromError } from "./connectorLock";
import FTSLabelGroup from './FTSLabelGroup';
import FTSDocumentModal from "./FTSDocumentModal.vue";
import FTSDocumentFormModal from "./FTSDocumentFormModal";
import FTSListViewPreferenceModal from "./FTSListViewPreferenceModal";
import FTSMyCalendarFormModal from "./FTSMyCalendarFormModal";
import FTSDropdownSelect from "./FTSDropdownSelect";
import FTSActionPageModal from './FTSActionPageModal';
import FTSWidgetDesignPreviewModal from '../dashboard/design/FTSWidgetDesignPreviewModal';
import FTSTestBadge from "./FTSTestBadge.vue";

const listViewModes = [
  { id: "list", code: "l" },
  { id: "card", code: "c" },
  { id: "board", code: "b" },
  { id: "calendar", code: "d" },
  { id: "detail", code: "dv" }
];
const BULK_MAX_COUNT = 50;
const BULK_EXCEED_ERROR = `Selected items exceeded. Maximum only allows to select up to ${BULK_MAX_COUNT}.`;

export default {
  components: {
    "fts-table-view": FTSTableView,
    "fts-card-view": FTSCardView,
    "fts-calendar-view": FTSCalendarView,
    "fts-detail-view": FTSDetailView,
    "fts-sample-records-banner": FTSSampleRecordsBanner,
    "fts-query-designer": FTSQueryDesigner,
    "fts-pagination": FTSPagination,
    "fts-tippy": FTSTippy,
    "fts-spinner": FTSSpinner,
    "fts-form": FTSForm,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-btn-hold": FTSBtnHold,
    "fts-btn": FTSBtn,
    "fts-sort": FTSSort,
    "fts-import": FTSImport,
    "fts-basket": FTSBasket,
    "fts-workflow-diagram": FTSWorkflowDiagram,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-connector-lock-card": FTSConnectorLockCard,
    "fts-label-group": FTSLabelGroup,
    "fts-document-modal": FTSDocumentModal,
    "fts-document-form-modal": FTSDocumentFormModal,
    "fts-list-view-preference-modal": FTSListViewPreferenceModal,
    "fts-my-calendar-form-modal": FTSMyCalendarFormModal,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-action-page-modal": FTSActionPageModal,
    "fts-widget-design-preview-modal": FTSWidgetDesignPreviewModal,
    "fts-test-badge": FTSTestBadge
  },
  props: {
    baseServer: String,
    propModuleId: [String, Number],
    token: String,
    onUpdated: Function,
    client: {
      type: Boolean,
      default: true,
    },
  },
  store: { classificationStore, canvasDesignStore },
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    mode: "list",
    header: {},
    columns: [],
    preferences: [],
    scroll: false,
    loading: false,
    schemaLoad: false,
    listViewLoaded: false,
    moduleId: null,
    moduleImport: null,
    listingId: null,
    listingKeyword: null,
    searchTerm: "",
    advancedFilter: false,
    // a filter edit is waiting for debouncedFilters to persist it (SS06080)
    filtersSavePending: false,
    listingError: false,
    // the module is on AutoCount Full and this workspace is not (402): shown as a lock, not an error
    connectorLock: null,
    filters: null,
    originalDefaultFilters: null,
    skipFiltersWatch: false,
    resultSet: { list: [] },
    sort: null,
    sortFilter: null,
    basketSelection: [],
    isBulk: false,
    pageSize: 20,
    page: 0,
    autoRefreshInterval: null,
    exportLoading: false
  }),
  /**
   * client route parameters
   * id - entry ID
   * l  - listing ID
   * m  - entry view mode
   * a  - activity ID
   * t  - list type
   * d  - document ID
   * v  - vector (module) ID
   * t* - custom params
   * dialog - dialog create form
   * text - dialog create form
   */
  async beforeRouteUpdate(to, from, next) {
    next();
    if (to.fullPath !== from.fullPath) {
      if (
        to.params.moduleId !== from.params.moduleId ||
        to.query.l !== from.query.l ||
        to.query.t !== from.query.t
      ) {
        this.initListViewMode({
          code: to.query.t,
          moduleId: commons.decodeids(to.params.moduleId),
          listingId: to.query.l
        });
      }
      if (!to.query.id && !to.query.a) {
        window.eventBus.$emit("closeQuickView");
      } else {
        var toTrigger = false;
        if (to.query.id && (!from.query || to.query.id != from.query.id)) {
          toTrigger = true;
        } else if (
          to.query.a == "create" && !to.query.dialog &&
          (
            from.query.id ||
            Object.keys(to.query).some(x => x && x.startsWith('t') && x.length > 1)
          )
        ) {
          toTrigger = true;
        } else if (to.query.a == "create") {
          //skip for create
        } else if (to.query.a && (!from.query || to.query.a != from.query.a)) {
          toTrigger = true;
        } else if (to.query.id && to.query.m !== from.query.m) {
          toTrigger = true;
        }

        if (toTrigger) {
          let entryId = null;
          let moduleId = null;
          let moduleUnspecified = false;
          if (to.query.id) {
            const queryDecodeids = commons.queryDecodeids(to.query.id);
            if (queryDecodeids && queryDecodeids.entryId) {
              entryId = queryDecodeids.entryId;
            }
            if (queryDecodeids && queryDecodeids.moduleId) {
              moduleId = queryDecodeids.moduleId;
            }
            moduleUnspecified = Boolean(queryDecodeids && queryDecodeids.moduleUnspecified);
            /* Only a deprecated (unprefixed) id implies "the module of this route". An id that
               explicitly encodes entry-only came from a wildcard connection, whose entry lives in
               another module — substituting this route's module makes the form request fail with
               "Module not match with entry". Leave it null and let the server resolve it (SS05672). */
            if (!moduleId && !moduleUnspecified && to.params.moduleId) {
              moduleId = commons.decodeids(to.params.moduleId);
            }
          } else if (to.query.a == "create" && to.params.moduleId) {
            moduleId = commons.decodeids(to.params.moduleId);
          }

          /* A listing belongs to this route's module; it cannot authorize an entry the server is
             about to resolve into a different one. */
          const listingId = moduleUnspecified ? null : to.query.l;

          if (this.mode === "detail") {
            this.$nextTick(() => {
              window.eventBus.$emit("loadDetailViewForm", {
                id: entryId,
                moduleId: moduleId,
                listingId: listingId,
                activityId: to.query.a,
                fullView: to.query.m && to.query.m.toLowerCase() === "f",
                fromUrl: true
              });
            });
          } else {
            window.eventBus.$emit("quickViewData", {
              id: entryId,
              vectorId: moduleId,
              listingId: listingId,
              activityId: to.query.a,
              fullView: to.query.m && to.query.m.toLowerCase() === "f",
              fromUrl: true,
            });
          }
        }
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    next();
    clientStore.dispatch("resetModule");
  },
  watch: {
    mode: async function (val, oldVal) {
      if (
        (val !== "detail" && oldVal === "detail") ||
        (val !== "calendar" && oldVal === "calendar")
      ) {
        if (this.designMode) {
          clientStore.dispatch("setDesignMode", { mode: false, skipLog: true });
        }

        if (!this.schemaLoad) {
          this.listViewLoaded = false;
          await this.initialSchema();
          this.listViewLoaded = true;
        } else {
          this.setInitialFilters();
          this.initialEntryView();
          this.retrieveItems();
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        this.debouncedFilters.cancel();

        if (this.skipFiltersWatch) return;

        this.filtersSavePending = true;
        this.debouncedFilters(val);
      }
    }
  },
  methods: {
    moment,
    isCapableOf(type) {
      return isCapableOf(type, this.workspaceModules, this.moduleId);
    },
    onScroll(e) {
      this.scroll = e.target.scrollTop > 0;
    },
    /* SS05576: the table's column headers stay put while the rows scroll. They have to
       stick BELOW .list-header, which is sticky itself and whose height changes with the
       filter and search rows - so publish the measured height as a custom property and let
       the CSS offset by it rather than hardcoding a guess. */
    syncListHeaderOffset() {
      const view = this.$refs.ftsListView;
      const header = this.$refs.listHeader;
      if (!view || !header) return;

      const apply = () => view.style.setProperty(
        "--fts-list-header-height", `${header.offsetHeight}px`);

      apply();

      if (typeof ResizeObserver === "undefined" || this.listHeaderResizeObserver) return;

      this.listHeaderResizeObserver = new ResizeObserver(apply);
      this.listHeaderResizeObserver.observe(header);
    },
    toListView(listing) {
      if (listing.script) {
        eval(listing.script);
      } else if (listing.id !== this.list.id) {
        this.$router.push({
          name: 'list-view',
          params: { moduleId: commons.encodeids(this.moduleId) },
          query: { l: listing.id }
        });
      }
    },
    resetScroll() {
      this.$refs.ftsListView.scrollTo({ top: 0, left: 0 });
    },
    setListViewMode(mode, { moduleId, listingId } = {}) {
      this.mode = listViewModes.some((x) => x.id === mode) ? mode : "list";
      if (moduleId) {
        let preference = {
          ...LocalStorageService.getModuleListingPreference(moduleId, listingId),
          type: this.mode
        }
        /* Edit mode is off unless this user turned it on here before: only an explicit
           designMode === true in the stored preference re-enters it. An absent key used to
           mean "on", which flipped every admin's first detail view into the designer. The
           internal gate matches the toggle's own (alpha), so a stale preference can never
           strand someone in edit mode with no icon to leave by. */
        if (!this.builderMode && this.isAdminUser && this.isInternal && this.mode === "detail") {
          if (preference && preference.designMode === true && !this.designMode) {
            this.toggleDesignMode(moduleId);
          }
        }
        LocalStorageService.setModuleListingPreference(moduleId, listingId, preference);
      }
      this.resetScroll();
      this.isBulk && this.setBulk();
    },
    initListViewMode({ code, moduleId, listingId } = {}) {
      let mode = null;
      if (this.client && code) {
        const listViewMode = listViewModes.find((x) => x.code === code.toLowerCase());
        Boolean(listViewMode && listViewMode.id) && (mode = listViewMode.id);
      }
      if (!mode) {
        const listViewPreference = LocalStorageService.getModuleListingPreference(moduleId, listingId);
        Boolean(listViewPreference && listViewPreference.type) && (mode = listViewPreference.type);
      }
      this.setListViewMode(mode, { moduleId, listingId });
    },
    composeFilterColumns(columns) {
      let filterColumns = _.cloneDeep(columns);
      // collaboration
      filterColumns.splice(1, 0, {
        ...queryLogic.querySelectionPrefix.find(x => x.id === "collaboration")
      });
      // assign to
      const assignToIndex = filterColumns.findIndex(x => x.id === "assignTo");
      if (assignToIndex > -1) {
        const isDueExisted = filterColumns.some(x => x.id === "due");
        if (!isDueExisted) {
          filterColumns.splice(assignToIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "due")
          });
        }
      }
      // created date
      const createdByIndex = filterColumns.findIndex(x => x.id === "createdBy");
      if (createdByIndex > -1) {
        const isCreatedDateExisted = filterColumns.some(x => x.id === "createdDate");
        if (!isCreatedDateExisted) {
          filterColumns.splice(createdByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "createdDate")
          });
        }
      }
      // last change date
      const lastChangeByIndex = filterColumns.findIndex(x => x.id === "lastChangeBy");
      if (lastChangeByIndex > -1) {
        const isLastChangeDateExisted = filterColumns.some(x => x.id === "lastChangeDate");
        if (!isLastChangeDateExisted) {
          filterColumns.splice(lastChangeByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "lastChangeDate")
          });
        }
      }
      
      return filterColumns.filter(column => {
        if (column.system) return true;

        const informationType = commons.informationTypes.find(x => x.id === column.type);
        return Boolean(informationType && informationType.searchable);
      });
    },
    submitDocument(action, documentFormData) {
      this.$v.documentFormData.$touch();

      if (this.$v.documentFormData.$invalid) {
        this.$toasted.global.invalidFormValidate();
        return;
      }

      let items = this.resultSet.list.filter((x) => x.selected);
      let payload = {
        document: action,
        items: items,
        moduleId: this.moduleId,
        listingId: this.listingId,
        filters: this.filters,
      };

      if (documentFormData != null) {
        let data = _.cloneDeep(documentFormData);
        for (var prop in data) {
          if (prop.indexOf("Model") > 0) {
            delete data[prop];
          }
        }
        parameters.inputs = data;
      }

      if (action.dataSource === "entry") {
        const entryItems = selectedItems.length ? selectedItems : items;
        if (action.allowSelection) {
          parameters.locusIds = entryItems.map((x) => x.id);
        } else {
          parameters.locusId = entryItems[0].id;
        }
      } else if (action.dataSource == "collection") {
        parameters.list = this.listingId;
      } else {
        /* logid service */
        parameters.logicService = action.logicServiceName;
        if (documentFormData == null) {
          parameters.inputs = {
            filters: this.filters,
            list: this.listingId,
          };

          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
          }
        }

        const entryItems = selectedItems.length ? selectedItems : items;
        if (action.allowSelection && entryItems) {
          parameters.inputs.locusIds = entryItems.map((x) => x.id);
        } else if (entryItems && entryItems.length > 0) {
          parameters.inputs.locusId = entryItems[0].id;
        }
      }
      let documentModel = { parameters: parameters };
      classificationStore.dispatch("updateDocumentId", null);
    },
    async pageChanged(page) {
      this.page = page;
      await this.retrieveItems();
      this.resetScroll();
    },
    setPageSizePreference(value) {
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId),
        pageSize: value
      };
      LocalStorageService.setModuleListingPreference(this.moduleId, this.listingId, preference);
    },
    /**
     * Resolve which listing to land on when the link omits the listing (?l=).
     * Admins / workspace admins have access to the "everything" listing
     * (appended to every module in clientStore.setWorkspace), so they go there.
     * Everyone else lands on the first listing they can access.
     */
    resolveDefaultListingId() {
      const module = this.module;
      if (!module || !Array.isArray(module.menus) || !module.menus.length) {
        return null;
      }

      if (this.isAdminUser) {
        const everything = module.menus.find((x) => x.id === "everything");
        if (everything) return everything.id;
      }

      // first accessible listing (skip script-only shortcut menus)
      const firstListing = module.menus.find((x) => !x.script);
      return firstListing ? firstListing.id : null;
    },
    initialEntryView() {
      if (
        this.$route.query.id ||
        (this.$route.query.a === "create" && this.isCreatable)
      ) {
        let entryId = null;
        let moduleId = this.moduleId;
        let listingId = this.$route.query.l;

        if (this.$route.query.id) {
          const queryDecodeids = commons.queryDecodeids(this.$route.query.id);
          if (queryDecodeids && queryDecodeids.entryId) {
            entryId = queryDecodeids.entryId;
          }
          if (queryDecodeids && queryDecodeids.moduleId) {
            moduleId = queryDecodeids.moduleId;
            if (this.moduleId != moduleId) {
              listingId = null;
            }
          } else if (queryDecodeids && queryDecodeids.moduleUnspecified) {
            /* Wildcard connection link: the entry is not this module's. Let the server resolve
               the module from the entry instead of asserting ours (SS05672). */
            moduleId = null;
            listingId = null;
          }
        }

        window.eventBus.$emit("quickViewData", {
          id: entryId,
          vectorId: moduleId,
          listingId: listingId,
          activityId: this.$route.query.a,
          fullView:
            this.$route.query.m &&
            this.$route.query.m.toLowerCase() === "f",
        });
      }
    },
    async loadWorkspaceList(payload = {}) {
      return await clientStore.dispatch("loadWorkspaceList", {
        moduleId: this.moduleId,
        listingId: this.listingId,
        withHeader: (payload && payload.withHeader) || false,
        currentPage: this.page,
        pageSize: this.pageSize,
        // the advanced filter only drives the result set while its toggle is on. With the
        // toggle off the query designer is hidden, so sending its filters -- the listing's
        // default filters included -- would keep narrowing the list with nothing on screen
        // to show or clear them. The filters stay in state, so toggling back on restores
        // them; they are just not queried on while the wildcard search is driving
        filters: (this.advancedFilter && ((payload && payload.filters) || null)) || null,
        sorts: this.sort ? this.sort : null,
        contextId: clientStore.state.workspaceContext != null ?
          clientStore.state.workspaceContext.id :
          null,
        // wildcard search only applies when the advanced-filter toggle is off; the
        // advanced filter (query designer) drives the result set otherwise
        search: (!this.advancedFilter && ((payload && payload.search) || this.searchTerm)) || null
      });
    },
    async refresh(payload = {}) {
      const withHeader = Boolean(payload && payload.withHeader);
      const withPreferenceFilters = Boolean(payload && payload.withPreferenceFilters);
      const preserveSelectedEntries = Boolean(payload && payload.preserveSelectedEntries);

      try {
        let res = null;

        if (withHeader) {
          let preference = null;
          let isPreferenceFilters = false;

          if (withPreferenceFilters) {
            preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
            isPreferenceFilters = Boolean(preference && preference.filters);

            // Restored filters must be active before the first list request. Otherwise
            // loadWorkspaceList strips them while advancedFilter is still false, then the
            // designer becomes visible after the unfiltered result has already returned.
            if (isPreferenceFilters) {
              this.advancedFilter = this.isCapableOf(SYSTEM_CAPABILITY.Query);
            }
          }

          // SS06120: what the header request really carries. loadWorkspaceList sends the filters
          // only while the advanced-filter toggle is on, and the search term only while it is
          // off. A header request with neither is answered with the listing's default filter
          // already applied (legacy /api/workspace/list and net10 /x/list both do that), so
          // its rows need no second fetch once the defaults are known.
          let serverAppliedDefaults =
            !(this.advancedFilter && this.filters) &&
            !(!this.advancedFilter && this.searchTerm);

          try {
            res = await this.loadWorkspaceList({ withHeader: true, filters: this.filters });
          } catch(err) {
            if (isPreferenceFilters) {
              // SS06080: retry without the saved filters first, and drop them only once the
              // server has accepted the same request without them - then the filters were
              // the problem (a field that no longer exists, say). If the retry fails too the
              // server was, and a transient error must not wipe what the user saved.
              isPreferenceFilters = false;
              res = await this.loadWorkspaceList({ withHeader: true });
              // the retry carries no filters at all
              serverAppliedDefaults = !(!this.advancedFilter && this.searchTerm);
              this.setLocalStoragePreferenceFilters(preference, null);
            } else {
              throw err;
            }
          }

          const resultData = _.cloneDeep(res.data);

          if (isPreferenceFilters) {
            const filterColumns = this.composeFilterColumns(resultData.header.columns);
            let clonedFilters = _.cloneDeep(this.filters);
            queryLogic.updateQueryFilters(filterColumns, clonedFilters);
            const diff = changesets.diff(clonedFilters, this.filters);

            if (diff && diff.length) {
              if (
                !Array.isArray(clonedFilters.items) || 
                !clonedFilters.items.length
              ) {
                clonedFilters = null;
              }

              try {
                res = await this.loadWorkspaceList({ filters: clonedFilters });
              } catch(err) {
                clonedFilters = null;
                res = await this.loadWorkspaceList();
              }

              if (clonedFilters) {
                this.setFiltersInternally(clonedFilters);
                this.setLocalStoragePreferenceFilters(preference, clonedFilters);
              } else {
                this.setFiltersInternally({
                  items: [
                    queryLogic.getDefaultQueryFilter(filterColumns, { workspaceBeta: clientStore.getters.workspaceBeta })
                  ]
                });
                
                isPreferenceFilters = false;
                this.setLocalStoragePreferenceFilters(preference, null);
              }
            }
          }

          this.header = resultData.header;
          this.columns = resultData.header.columns;
          this.moduleImport = resultData.import;
          // open in advanced-filter mode when a default filter is configured or the user has
          // filters saved from a previous visit (and they can use it); otherwise start with
          // the simpler wildcard search. Restored filters must come back with the designer
          // visible -- it is what shows and clears them
          this.advancedFilter =
            (this.hasDefaultFilter || isPreferenceFilters) &&
            this.isCapableOf(SYSTEM_CAPABILITY.Query);

          if (
            this.header.defaultFilters && 
            this.header.defaultFilters.items && 
            this.header.defaultFilters.items.length
          ) {
             const { filters, originalDefaultFilters, defaultFilterChanged } = listing.setDefaultFilters(
              this.header.defaultFilters,
              this.originalDefaultFilters,
              this.filters,
              this.columns
            );

            this.originalDefaultFilters = originalDefaultFilters;

            if (!isPreferenceFilters) {
              this.setFiltersInternally(filters);

              // A listing's default filters are only known after the header returns. If
              // that request carried a query of its own (the filters on screen, or a search
              // term), its rows answer that query, so fetch them again with the newly
              // resolved defaults -- but only when those defaults actually constrain the
              // query. A listing may seed a placeholder row (a field and operator with no
              // payload) just to open the query designer ready to fill in; the server
              // ignores an incomplete condition, so refetching on one returns the identical
              // result set. A header request with no query of its own needs no refetch
              // either: the server applied these same defaults to it (SS06120).
              if (
                this.advancedFilter &&
                defaultFilterChanged &&
                !serverAppliedDefaults &&
                queryLogic.getFiltersValidity(filters)
              ) {
                res = await this.loadWorkspaceList({ filters: filters });
              }
            } else if (defaultFilterChanged) {
              const reseated = await this.reseatDesignOwnedFilters(preference, this.header.defaultFilters);
              if (reseated) res = reseated;
            }
          } else if (isPreferenceFilters) {
            // the definition has no units left: a preference must not keep replaying old ones
            const reseated = await this.reseatDesignOwnedFilters(preference, { items: [] });
            if (reseated) res = reseated;
          } else if (!this.schemaLoad) {
            this.setFiltersInternally({
              items: [
                queryLogic.getDefaultQueryFilter(this.columns, { workspaceBeta: clientStore.getters.workspaceBeta })
              ]
            });
            this.originalDefaultFilters = null;
          }
          // default sort
          if (this.header.defaultSort) {
            this.sort = _.cloneDeep(this.header.defaultSort);
          }
          // system capability
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
            this.header.standards = this.header.standards.filter(x =>
              ["create", "edit", "changeStatus", "duplicate", "assign"].indexOf(x) === -1
            );
          }
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
            this.header.standards = this.header.standards.filter(x => x !== "delete");
          }
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
            this.header.standards = this.header.standards.filter(x => ["view", "quickView"].indexOf(x) === -1);
          }
          // preferences
          this.originalPreferences = this.header.preferences;
          this.preferences = this.header.preferences;
          if (this.preferences && this.preferences.length) {
            const diffColumns = _.differenceBy(this.columns, this.preferences, 'id').map(x => ({ id: x.id }));
            const diffPreferences = _.differenceBy(this.preferences, this.columns, 'id');
            if (diffColumns.length) {
              this.preferences = this.preferences.concat(diffColumns);
            }
            if (diffPreferences.length) {
              diffPreferences.forEach(x => {
                this.preferences.splice(this.preferences.indexOf(x), 1);
              });
            }
          }
          // auto refresh
          if (this.autoRefreshInterval) {
            clearInterval(this.autoRefreshInterval);
            this.autoRefreshInterval = null;
          }

          if (this.header && this.header.autoUpdate && !this._isBeingDestroyed && !this._isDestroyed) {
            this.autoRefreshInterval = setInterval(async () => {
              if (!this.loading) {
                const cloneSelectedIds = this.formData.filter((x) => x.selected).map((x) => x.id);
                await this.retrieveItems();
                if (cloneSelectedIds) {
                  cloneSelectedIds.forEach((x) => {
                    let res = this.formData.find((y) => y.id === x);
                    res && this.$set(res, "selected", true);
                  });
                }
              }
            }, 5000);
          }

          this.schemaLoad = true;
        } else {
          res = await this.loadWorkspaceList({ filters: this.filters });
        }

        let resultSet = res.data.data;

        if (
          preserveSelectedEntries && 
          this.resultSet && 
          this.resultSet.list && 
          this.resultSet.list.length
        ) {
          const selectedIds = {};
          for (let i = 0; i < this.resultSet.list.length; i++) {
            const item = this.resultSet.list[i];
            if (item.selected) selectedIds[item.id] = true;
          }

          for (let i = 0; i < resultSet.list.length; i++) {
            if (selectedIds[resultSet.list[i].id]) {
              resultSet.list[i].selected = true;
            }
          }
        }

        this.resultSet = resultSet;
        this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.close();
        this.listingError = false;
        this.connectorLock = null;
      } catch (err) {
        console.error(err);
        this.listingError = true;
        this.connectorLock = connectorLockFromError(err);
        if (this.connectorLock) {
          this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.close();
          return;
        }
        this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          title: "Something went wrong",
          response: err.response,
          disabledAction: true
        });
      }
    },
    async initialSchema() {
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel();
      }
      this.searchTerm = "";
      this.setInitialFilters();
      await this.refresh({ withHeader: true, withPreferenceFilters: true });

      if (!this.listingError) {
        this.initialEntryView();
      }
    },
    setFiltersInternally(filters) {
      this.debouncedFilters.cancel();
      this.filtersSavePending = false;
      this.skipFiltersWatch = true;
      this.filters = filters;
      this.$nextTick(() => {
        this.skipFiltersWatch = false;
      });
    },
    /* ---------------- AI TeamMate view bridge ---------------- */
    // Snapshot of what the user is looking at, handed to the chat so it can ground
    // answers in (and extend) this exact view. The filter is the { items: [...] }
    // designer schema, which the backend list_entries accepts verbatim via filterSchema.
    // Independent views (calendar/detail) carry no list filter, so share nothing.
    getTeammateView() {
      if (this.isIndependentView) return null;
      const view = {
        moduleId: this.moduleId,
        listingId: this.listingId,
      };
      if (this.module && this.module.name) view.module = this.module.name;
      if (this.list && this.list.name) view.listing = this.list.name;
      // Listing's own emoji so the chat's List context chip can wear it (like the module chip).
      if (this.list && this.list.emoji) view.listingEmoji = this.list.emoji;
      if (this.filters && Array.isArray(this.filters.items) && this.filters.items.length) {
        view.filter = _.cloneDeep(this.filters);
      }
      return view;
    },
    // Apply a teammate-returned view to THIS list — only when it targets the module +
    // listing we're already showing. Cross-listing navigation is driven chat-side
    // (see consumePendingTeammateView for the post-navigation apply).
    applyTeammateView(view) {
      if (!view || this.isIndependentView) return;
      const sameModule =
        (view.module && this.module && view.module === this.module.name) ||
        (view.moduleId && view.moduleId == this.moduleId);
      const sameListing =
        view.listingId == this.listingId ||
        ((view.listingId === "everything" || !view.listingId) &&
          (this.listingId === "everything" || !this.listingId));
      if (!sameModule || !sameListing) return;
      if (view.filter && Array.isArray(view.filter.items)) {
        this.setFiltersInternally(view.filter);
        const preference = {
          ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)
        };
        this.setLocalStoragePreferenceFilters(preference, view.filter);
        this.retrieveItems();
      }
    },
    // After a chat-driven navigation, apply the view the chat stashed for this list.
    consumePendingTeammateView() {
      const view = window.__teammatePendingView;
      if (!view) return;
      const sameModule =
        (view.module && this.module && view.module === this.module.name) ||
        (view.moduleId && view.moduleId == this.moduleId);
      if (!sameModule) return;
      window.__teammatePendingView = null;
      this.applyTeammateView(view);
    },
    // SS06080: a filter edit is persisted by debouncedFilters one second after the last
    // change (the same tick refetches the rows). Leaving the listing inside that second -
    // the listing dropdown, the back button - destroyed this component and cancelled the
    // save, so the filters the user had just built were gone when they came back and the
    // listing opened in Wildcard Search. Persist the pending edit instead of dropping it;
    // the refetch it would have triggered is moot for a component that is going away.
    flushPendingFilterSave() {
      if (!this.filtersSavePending) return;
      this.filtersSavePending = false;

      if (this.debouncedFilters && this.debouncedFilters.cancel) {
        this.debouncedFilters.cancel();
      }

      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)
      };
      this.setLocalStoragePreferenceFilters(preference, this.filters);
    },
    setLocalStoragePreferenceFilters(preference, filters) {
      let savedPreference = { ...preference };

      // SS05932 - a row with no usable value (e.g. "contains" with its text cleared)
      // constrains nothing, so it is not a filter worth restoring: persisting it would
      // reopen Advanced Filter mode on the next load for a query that is not actually
      // happening. getFiltersValidity is the same predicate refresh() trusts before
      // refetching for default filters; value-less operators (isNone, me, yes, ...) count.
      if (
        filters &&
        Array.isArray(filters.items) &&
        filters.items.length > 0 &&
        queryLogic.getFiltersValidity(filters)
      ) {
        savedPreference.filters = filters;
      } else {
        delete savedPreference.filters;
      }

      LocalStorageService.setModuleListingPreference(
        this.moduleId,
        this.listingId,
        savedPreference
      );
    },
    /* SS05684: a saved preference replays the design-owned units (script / formula) with the
       definition they had when it was saved, and the server runs the script the client posts.
       Re-seat those units from the definition the header just delivered, keep the user's own
       units and values, persist, and requery - only when that actually changed something. */
    async reseatDesignOwnedFilters(preference, definitionFilters) {
      const reseated = listing.refreshDesignOwnedUnits(this.filters, definitionFilters);
      if (!reseated.changed) {
        return null;
      }

      this.setFiltersInternally(reseated.filters);
      this.setLocalStoragePreferenceFilters(preference, reseated.filters);
      return this.loadWorkspaceList({ filters: reseated.filters });
    },
    setInitialFilters() {
      let preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);

      if (preference && preference.filters) {
        if (
          Array.isArray(preference.filters.items) &&
          preference.filters.items.length &&
          queryLogic.getFiltersValidity(preference.filters)
        ) {
          this.setFiltersInternally(preference.filters);
        } else {
          // SS05932 - also drops a saved set that no longer constrains anything (earlier
          // builds persisted blank rows), so refresh() sees no saved filters and opens in
          // Wildcard Search rather than Advanced Filter.
          this.setLocalStoragePreferenceFilters(preference, null);
        }
      }
    },
    async retrieveItems(payload) {
      this.loading = true;
      await this.refresh(payload);
      this.loading = false;
    },
    onSearchInput() {
      this.debouncedSearch.cancel();
      this.debouncedSearch();
    },
    clearSearch() {
      if (!this.searchTerm) return;
      this.debouncedSearch.cancel();
      this.searchTerm = "";
      this.page = 0;
      this.retrieveItems();
    },
    toggleAdvancedFilter() {
      this.advancedFilter = !this.advancedFilter;
      this.page = 0;

      // entering advanced filter: drop any pending wildcard search so the query
      // designer alone drives the result set
      if (this.advancedFilter) {
        if (this.debouncedSearch && this.debouncedSearch.cancel) {
          this.debouncedSearch.cancel();
        }
        this.searchTerm = "";
      } else {
        // SS05932: leaving advanced filter is a deliberate choice and has to survive a
        // reload. Restoring on load keys off the SAVED FILTERS (isPreferenceFilters, see
        // loadList), so leaving them in localStorage put the user straight back into
        // advanced mode on refresh - "Switch to search" looked like it did nothing.
        //
        // Cancel the pending filter save first: editing a filter and switching to search
        // inside its 1s debounce window would otherwise re-save the filters right after
        // this clear and reinstate the very state we are clearing.
        if (this.debouncedFilters && this.debouncedFilters.cancel) {
          this.debouncedFilters.cancel();
        }
        // ...and nothing must persist it later either (SS06080 flushes a pending edit
        // when the listing is left).
        this.filtersSavePending = false;

        const preference = LocalStorageService.getModuleListingPreference(
          this.moduleId,
          this.listingId
        );

        if (preference) {
          this.setLocalStoragePreferenceFilters(preference, null);
        }

        // this.filters is deliberately left alone - toggling back within the same session
        // should still show what the user built. Only the persisted copy is dropped.
      }

      this.retrieveItems();
    },
    setBulk() {
      this.isBulk = !this.isBulk;
      if (!this.isBulk)
        this.formData.forEach((x) => this.$delete(x, "selected"));
    },
    createEntry(fullView) {
      this.executeActivity({ id: "create" }, [], fullView);
    },
    executeActivity(activity, list, fullView) {
      if (list.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      if (activity.skipConfirm && !activity.standard) {
        window.eventBus.$emit("formSkipConfirmation", {
          moduleId: this.moduleId && this.moduleId.toString(),
          activity: activity,
          activityId: activity.id,
          entryIds: list.map((x) => x.id)
        });
      } else {
        window.eventBus.$emit("quickViewData", {
          items: list,
          vectorId: this.moduleId,
          listingId: this.list.id,
          activityId: activity.id,
          // The portal page this activity opens instead of the form, if any (activityPortal.js).
          portalId: activityPortalId(activity, this.header),
          fullView: fullView,
        });
      }
    },
    /**
     * @param {Object} action
     * @param {Array} items eval(script)
     */
    async executeAction(action, items) {
      commons.executeEntryCustom({
        action: action,
        items: items,
        modal: this.$refs.ftsActionPageModal
      });
    },
    async executeDocument(action, items) {
      if (items.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      let vm = this;
      if (action.type == "template") {
        let template = {
          templateId: action.id,
          entryId: items[0].id,
        };

        this.$refs.documentModal.show(template);
      } else if (action.hasForm) {
        let items = this.resultSet.list.filter((x) => x.selected);
        await this.$refs.ftsDocumentFormModal.show({
          moduleId: this.moduleId,
          listingId: this.listingId,
          document: action,
          items: items,
          filters: this.filters
        });
      } else {
        let parameters = {
          document: action,
          items: items,
          moduleId: this.moduleId,
          listingId: this.listingId,
          filters: this.filters,
        };

        if (action.dataSourceId == "logicService") {
          if (!parameters.inputs) {
            parameters.inputs = {};
          }

          let items = this.resultSet.list.filter((x) => x.selected);
          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
            parameters.inputs.locusIds = items.map(x => x.id);
          }
        }

        window.eventBus.$emit("showPDFDocument", parameters);
      }
    },
    async exportData() {
      if (this.exportLoading) {
        return;
      }

      // SS05666: `sorts` is the same value the list query sends, so the file comes out ordered
      // the way the list is on screen. Shared builder — the listing toolbar posts the same shape.
      // SS06127: likewise the filters or search box term the list query is narrowed by.
      const scope = exportQueryScope({
        advancedFilter: this.advancedFilter,
        filters: this.filters,
        searchTerm: this.searchTerm,
      });
      const payload = buildExportPayload({
        listingId: this.listingId,
        moduleId: this.moduleId,
        filters: scope.filters,
        search: scope.search,
        sorts: this.sort,
        contextId:
          clientStore.state.workspaceContext != null
            ? clientStore.state.workspaceContext.id
            : null,
      });
      let isDirectDownload = this.totalItems < Export_Direct_Download_Threshold;

      if (!isDirectDownload) {
        const confirmation = await this.$confirmation.show({
          title: "Export Confirmation",
          textHTML: `<p>This export contains ${Export_Direct_Download_Threshold} records or more.</p><p>You can export the <strong>first ${Export_Direct_Download_Threshold} records</strong> now, or request the report to be delivered at midnight.</p><ul style="margin-top: 6px;margin-bottom: 6px;padding-left: 16px;"><li><strong>Export now (${Export_Direct_Download_Threshold})</strong></li><li><strong>Request Data</strong></li></ul>`,
          buttons: [
            {
              title: "Cancel",
              bind: { text: true }
            },
            {
              title: "Export",
              bind: { outline: true, color: "primary" },
              handler: ({ resolve }) => resolve("direct")
            },
            {
              title: "Request Data",
              default: true,
              handler: ({ resolve }) => resolve("request")
            }
          ]
        });

        if (confirmation === "dismiss") {
          return;
        }

        isDirectDownload = confirmation === "direct";
      }

      this.exportLoading = true;

      try {
        if (isDirectDownload) {
          const res = await clientStore.dispatch("exportData", {
            moduleId: this.moduleId,
            query: payload
          });
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "export.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          await clientStore.dispatch("exportRequest", {
            moduleId: this.moduleId,
            query: payload,
          });

          this.$refs.ftsExportFeedbackIndicate && this.$refs.ftsExportFeedbackIndicate.show({
            type: "success",
            desc: "Request submitted. Report will be sent midnight."
          });
        }
      } catch (err) {
        this.$toasted.global.error(
          err,
          isDirectDownload
            ? "Failed to export data. Please try again later."
            : "Failed to request data. Please try again later."
        );
      } finally {
        this.exportLoading = false;
      }
    },
    importData() {
      this.$refs.ftsImport.open();
    },
    async openUserPreferenceModal() {
      let columns = _.cloneDeep(this.columns);
      if (this.preferences && this.preferences.length) {
        columns = this.preferences.map((x) => {
          var column = this.columns.find((y) => y.id == x.id);
          return { ...x, ...column };
        });
      }

      const result = await this.$refs.ftsListViewPreferenceModal.show(
        columns,
      );

      if (result !== true) {
        await classificationStore.dispatch("saveUserPreference", {
          workspace: this.workspaceName,
          vector: this.moduleId,
          list: this.listingId,
          // mode: this.mode,
          mode: 'list',
          columns: result,
        });
        this.preferences = result;
        /* Saving a column preference changes which lane may serve this listing: /x/list
           does not honour user preferences, so the server stops advertising x for it (and
           refuses the request outright). The workspace copy in memory was fetched before
           this save, so patch the flag there too -- otherwise the next visit to this
           listing still asks /x/list and comes back with the DEFAULT columns, which reads
           as the preference having reset itself (SS06055). Clearing the preference
           (result === null) hands the listing back, as far as the flags in hand allow --
           if the bootstrap has since run without x, the next one restores it. */
        clientStore.dispatch("syncModuleListing", {
          moduleId: this.moduleId,
          listingId: this.listingId,
          data: { id: this.listingId, preference: Array.isArray(result) },
        });
      }
    },
    updateBasket(data) {
      this.basketSelection = data;
    },
    workflow() {
      this.$refs.ftsWorkflowDiagram.show();
    },
    async openMyCalendarFormModal() {
      this.$refs.ftsMyCalendarFormModal.show();
    },
    async toggleDesignMode(moduleId) {
      if (this.designModeLoading) {
        return;
      }
      if (!this.designMode) {
        try {
          clientStore.dispatch("setDesignModeLoading", true);
          await canvasDesignStore.dispatch("loadVector", moduleId);
          clientStore.dispatch("setDesignMode", { mode: true });
        } catch (err) {
          console.error(err);
          canvasDesignStore.dispatch("resetVector");
          this.$toasted.global.error({
            response: err.response,
            message: 'Unable to turn on edit mode'
          });
        } finally {
          clientStore.dispatch("setDesignModeLoading", false);
        }
      } else {
        canvasDesignStore.dispatch("resetVector");
        clientStore.dispatch("setDesignMode", { mode: false });
      }
    },
    openWidgetDesignPreviewModal() {
      this.$refs.ftsWidgetDesignPreviewModal.show({
        moduleId: this.moduleId,
        listingId: this.listingId,
        filters: this.filters,
        source: "listView"
      });
    },
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    hasDefaultFilter() {
      return Boolean(
        this.header &&
        this.header.defaultFilters &&
        Array.isArray(this.header.defaultFilters.items) &&
        this.header.defaultFilters.items.length
      );
    },
    isBeta() {
      return authorizationStore.getters.isBeta || this.workspaceBeta;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    isCreatable() {
      return this.standardActivities && this.standardActivities.indexOf("create") > -1;
    },
    isExportRight() {
      // temp to disable for everything listing
      if (this.listingId === "everything" && !this.isInternal)
        return false;

      return this.header.standards && this.header.standards.indexOf('export') > -1;
    },
    isDashboardWidgetRight() {
      // temp to disable for everything listing
      if (this.listingId === "everything" && !this.isInternal)
        return false;

      return true;
    },
    designMode() {
      return !this.builderMode && clientStore.getters.designMode;
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceName() {
      return clientStore.getters.workspaceName;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceBuilder() {
      return clientStore.getters.workspaceBuilder;
    },
    workspaceBeta() {
      return clientStore.getters.workspaceBeta;
    },
    workspaceContext() {
      return clientStore.state.workspaceContext;
    },
    module() {
      return clientStore.getters.workspaceModules.find((x) => x.id == this.moduleId);
    },
    moduleListingOptions() {
      return this.listingKeyword ?
        this.module.menus.filter((x) =>
          x.name && x.name.toLowerCase().indexOf(this.listingKeyword.toLowerCase()) !== -1
        ) :
        this.module.menus;
    },
    list() {
      return clientStore.state.list;
    },
    states() {
      return clientStore.getters.moduleStates;
    },
    users() {
      return clientStore.getters.workspaceUsers;
    },
    isIndependentView() {
      return ["calendar", "detail"].includes(this.mode);
    },
    createActivityUrl() {
      return commons.clientPathUrl({
        moduleId: this.moduleId,
        listId: this.listingId,
        activity: "create",
      });
    },
    standardActivities() {
      return this.header && this.header.standards;
    },
    bulkActivities() {
      if (this.isBulk && this.resultSet.list.filter((x) => x.selected).length) {
        let standardActivities = _.intersectionWith(
          commons.standardActivities.filter((x) => x.bulk),
          this.standardActivities,
          ({ id }, key) => id === key
        );
        return standardActivities;
      }
      return [];
    },
    activityList() {
      if (this.resultSet && this.header && this.header.activities) {
        const selectedItems = this.resultSet.list.filter((x) => x.selected);
        if (this.mode == "list" && selectedItems.length) {
          return this.header.activities.filter((activity) => {
            if (selectedItems.length > 1 && activity.disableBulkActions)
              return false;
            if (activity.fromStates && activity.fromStates.length) {
              return selectedItems.every((item) =>
                activity.fromStates.some((y) => y == entryStateId(item.state))
              );
            }
            return true;
          });
        }
      }
      return [];
    },
    actionList() {
      /* custom visibility */
      if (this.resultSet && this.header && this.header.customs) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.customs.filter((action) => {
          if (Boolean(action.condition)) {
            try {
              return Boolean(eval(action.condition));
            } catch (err) {
              console.error('[Custom:Condition error]', err);
              return false;
            }
          }

          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              return action.isMultiple;
            } else {
              return true;
            }
          }

          return action.isDefault;
        });
      }
      return [];
    },
    documentList() {
      if (this.resultSet && this.header && this.header.documents) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.documents.filter((action) => {
          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              // Multi-select: legacy allowSelection docs, PLUS htmljs single-entry ("entry type")
              // documents — those combine into ONE PDF across the selection (bulk print, §5d).
              // Collection/report docs (dataSourceId "collection") are whole-list, so stay excluded.
              return (
                action.allowSelection ||
                (action.engine === "htmljs" && action.dataSourceId !== "collection")
              );
            } else {
              return true;
            }
          }

          return (
            action.dataSourceId == "collection" ||
            action.dataSourceId == "logicService"
          );
        });
      }
      return [];
    },
    filterColumns() {
      let filterColumns = this.composeFilterColumns(this.columns);

      // query selection suffix
      const querySelectionSuffix = queryLogic.querySelectionSuffix
        .filter(x => x.fieldName === "and" || x.fieldName === "or")
        .map(y => (({ ...y, system: true })));

      filterColumns = filterColumns.concat(querySelectionSuffix);

      return filterColumns;
    },
    formData() {
      return this.resultSet.list ? this.resultSet.list : [];
    },
    totalItems() {
      return this.resultSet?.totalItems ?? 0;
    },
    totalItemslimit() {
      const limit = this.resultSet && this.resultSet.limit;

      if (limit && commons.isInteger(limit) && limit > 0) {
        return limit;
      }

      return Listing_Max_Total_Entries;
    },
    isTotalItemsExceed() {
      return this.totalItems >= this.totalItemslimit;
    },
    /* SS05909: the search the rows on screen answer to - none while the advanced filter is on,
       which is when loadWorkspaceList leaves the search box out of the request. */
    appliedSearchTerm() {
      return (!this.advancedFilter && this.searchTerm) || '';
    },
    getColumns() {
      if (Array.isArray(this.preferences) && this.preferences.length) {
        return this.preferences.reduce((previousValue, currentValue) => {
          const column = this.columns.find(x => x.id === currentValue.id);
          return column ? [...previousValue, { ...currentValue, ...column }] : previousValue;
        }, [])
      }
      return this.columns;
    },
    dateTimeColumns() {
      return this.getColumns
        .filter(
          x => (x.type === 5 || x.type === 6) && !x.system
        )
        .concat([
          {
            id: "createdDate",
            displayName: "Created Date",
            fieldName: "createdDate",
            system: true,
          },
          {
            id: "updatedDate",
            displayName: "Last Change Date",
            fieldName: "updatedDate",
            system: true,
          },
        ]);
    },
    designModeLoading() {
      return clientStore.getters.designModeLoading;
    },
    designModeState() {
      return clientStore.getters.designModeState;
    }
  },
  created() {
    this.builderMode = commons.builderMode;

    this.refreshSchemaHandler = () => {
      if (!this.isIndependentView) {
        this.refresh({ withHeader: true, withPreferenceFilters: true });
      }
    }

    this.refreshListHandler = (payload = {}) => {
      if (!this.isIndependentView && (!payload.moduleId || payload.moduleId == this.moduleId)) {
        let option = {};
        if (payload.preserveSelectedEntries) {
          option.preserveSelectedEntries = true;
        }

        this.retrieveItems(option);
      }
    }

    this.debouncedFilters = _.debounce(async (filters) => {
      this.filtersSavePending = false;
      this.page = 0;
      // preference
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)
      }
      this.setLocalStoragePreferenceFilters(preference, filters);
      // end preference
      await this.retrieveItems();
    }, 1000);

    this.debouncedSearch = _.debounce(async () => {
      this.page = 0;
      await this.retrieveItems();
    }, 400);

    // AI TeamMate view bridge: collect (synchronous pull via callback) + apply.
    this.collectTeammateViewHandler = (cb) => {
      if (typeof cb === "function") cb(this.getTeammateView());
    };
    this.applyTeammateViewHandler = (view) => this.applyTeammateView(view);

    window.eventBus.$on("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$on("listing:refresh", this.refreshListHandler);
    window.eventBus.$on("entry:updated", this.refreshListHandler);
    window.eventBus.$on("teammate:collectView", this.collectTeammateViewHandler);
    window.eventBus.$on("teammate:applyView", this.applyTeammateViewHandler);
  },
  async mounted() {
    this.moduleId = commons.decodeids(this.$route.params.moduleId);
    this.listingId = this.$route.query.l;
    this.loading = true;
    this.listViewLoaded = false;
    // get preference
    const preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
    if (preference && preference.pageSize) {
      this.pageSize = preference.pageSize;
    }

    try {
      await clientStore.dispatch("loadWorkspaceModule", {
        moduleId: this.moduleId,
        listId: this.listingId,
      });
      /* set canvas design, classification users */
      canvasDesignStore.dispatch("setUsers", this.users);
      classificationStore.dispatch("setUsers", { users: this.users });
      /* module undefined */
      if (this.moduleId != clientStore.getters.moduleId) {
        throw {
          isCustom: true,
          message: "Module isn't available. The module you're trying to access is unpublished or have been removed."
        };
      }

      /* deep-link without an explicit listing (?l=): route to the listing the
         user can access — "everything" for admins, first listing otherwise */
      if (!this.listingId) {
        const defaultListingId = this.resolveDefaultListingId();
        if (defaultListingId) {
          this.listingId = defaultListingId;
          await this.$router.replace({
            name: "list-view",
            params: { moduleId: this.$route.params.moduleId },
            query: { ...this.$route.query, l: defaultListingId },
          }).catch(() => { });
          /* reload so state.list reflects the resolved listing */
          await clientStore.dispatch("loadWorkspaceModule", {
            moduleId: this.moduleId,
            listId: this.listingId,
          });
        }
      }

      if (!this.builderMode && this.isAdminUser && clientStore.getters.designMode && this.moduleId != canvasDesignStore.state.vectorId) {
        try {
          await canvasDesignStore.dispatch("loadVector", this.moduleId);
        } catch (err) {
          console.error(err);
          canvasDesignStore.dispatch("resetVector");
          clientStore.dispatch("setDesignMode", { mode: false });
        }
      }

      if (this.$route.query.dialog) {
        const activity = this.$route.query.a;
        const locusId = commons.decodeids(this.$route.query.id);
        const fullView = this.$route.query.m && this.$route.query.m.toLowerCase() === "f";
        if (activity === "create") {
          this.createEntry(fullView);
        } else if (locusId) {
          this.executeActivity(
            { id: "quickView" },
            [{ id: locusId }],
            fullView
          );
        }
        this.listViewLoaded = true;
      } else {
        this.initListViewMode({
          code: this.$route.query.t,
          moduleId: this.moduleId,
          listingId: this.listingId
        });

        if (this.isIndependentView) {
          this.listViewLoaded = true;
        } else {
          try {
            await this.initialSchema();
            this.listViewLoaded = true;
            // If the chat navigated here to show a filtered view, apply it now that
            // the schema + default filters are loaded (this overrides them).
            this.consumePendingTeammateView();
          } catch (err) { }
        }
      }
    } catch (err) {
      this.connectorLock = connectorLockFromError(err);
      if (this.connectorLock) {
        // AutoCount Full's lock is drawn by the card above, not as an error.
      } else if (err.isCustom) {
        this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          title: err.title,
          desc: err.message,
          disabledAction: true,
        });
      } else {
        this.$refs.ftsFeedbackIndicate && this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          title: "Something went wrong",
          response: err.response,
          disabledAction: true,
        });
      }
    } finally {
      this.loading = false;
    }

    this.$nextTick(() => this.syncListHeaderOffset());
  },
  beforeDestroy() {
    if (this.listHeaderResizeObserver) {
      this.listHeaderResizeObserver.disconnect();
      this.listHeaderResizeObserver = null;
    }

    window.eventBus.$off("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$off("listing:refresh", this.refreshListHandler);
    window.eventBus.$off("entry:updated", this.refreshListHandler);
    window.eventBus.$off("teammate:collectView", this.collectTeammateViewHandler);
    window.eventBus.$off("teammate:applyView", this.applyTeammateViewHandler);
    clearInterval(this.autoRefreshInterval);
    this.autoRefreshInterval = null;

    this.flushPendingFilterSave();

    if (this.debouncedFilters && this.debouncedFilters.cancel) {
      this.debouncedFilters.cancel();
    }

    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

$list-view-min-width: 768px;

.fts-list-view {
  height: 100%;
  padding-bottom: 1rem;
  overflow: auto;

  .list-header {
    @include theme() {
      @include backdrop-background($color: theme-get('fts-background'),
        $alpha: 0.72,
        $blur: 20px);
    }

    position: sticky;
    top: 0;
    left: 0;
    min-width: $list-view-min-width;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    color: var(--fts-text);
    z-index: calc(#{$z-index-sticky} + 1);

    &.scrolled {
      box-shadow: 0 0 4px 0 var(--fts-gray-200);
    }

    .list-nav {
      display: flex;
      align-items: center;
      height: 45px;
      margin-left: 1rem;
      margin-right: 1rem;

      .list-nav-l {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        margin-right: 1rem;
        cursor: default;

        .list-nav-title-select {
          min-width: 0;
          border-bottom: 1px dotted var(--fts-border);
        }

        .list-nav-title {
          display: flex;
          align-items: center;
          font-size: 1rem;
          color: var(--fts-text);

          .list-nav-listing {
            margin-left: 0.75rem;
          }
        }

        .list-nav-module-tag {
          height: 1.125rem;
          width: 1.125rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 0.5rem;
          font-size: 0.675rem;
          border-radius: 0.25rem;
          background-color: var(--drawer-tag-background, var(--drawer-secondary-background));
          color: var(--drawer-tag, var(--drawer-secondary));
        }
      }

      .list-nav-r {
        .list-nav-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;

          >*:not(:last-child) {
            margin-right: 1rem;
          }
        }

        .list-nav-action {
          display: flex;
          font-size: 1.125rem;
          color: var(--fts-default);
          cursor: pointer;

          &.list-nav-action-fa {
            font-size: 1rem;
          }
        }
      }
    }

    .list-actions {
      padding: 0.5rem 1rem;
    }
  }

  .list-view-content-header {
    position: sticky;
    left: 1rem;
    min-width: $list-view-min-width;
    margin-top: 0.5rem;
    margin-left: 1rem;
    margin-right: 1rem;
    margin-bottom: 1rem;
    z-index: 1;
  }

  .list-view-content-header-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .list-view-system-filter {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--fts-border);
  }

  .list-view-control {
    flex: 1;
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    margin-left: 0.5rem;

    .list-view-search {
      position: relative;
      display: flex;
      align-items: center;
      margin-right: auto;

      .list-view-search-input {
        min-width: 180px;
        max-width: 280px;
        padding-right: 2rem;
        border-radius: 4px;
      }

      .list-view-search-action {
        position: absolute;
        top: 50%;
        right: 0.75rem;
        transform: translateY(-50%);
        font-size: 0.75rem;
        color: var(--fts-default);

        &.list-view-search-action--search {
          pointer-events: none;
        }

        &.list-view-search-action--clear {
          cursor: pointer;
        }
      }
    }

    .list-view-control-icon {
      flex-shrink: 0;
      font-size: 1.125rem;
      color: var(--fts-default);

      .tippy-trigger {
        cursor: pointer;
      }

      &:not(:first-child) {
        margin-left: 0.75rem;
      }

      &.list-view-control-icon--active {
        color: var(--fts-primary);
      }

      &.list-view-control-icon--loading {
        cursor: progress;
        color: var(--fts-disabled);

        .tippy-trigger {
          cursor: progress;
        }
      }
    }
  }

  /* footer control */
  .list-view-footer-control {
    position: sticky;
    left: 1rem;
    margin-top: calc(0.5rem + 0.375rem);
    margin-left: 1px;
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-text);

    &.list-view-footer-control--addable {
      cursor: pointer;

      &:hover {
        @include theme {
          background-color: rgba(theme-get('fts-primary'), 0.125);
        }

        color: var(--fts-primary);
      }
    }

    &+.list-view-pagination {
      margin-top: 0.5rem;
    }
  }

  /* pagination */
  .list-view-pagination {
    position: sticky;
    left: 1rem;
    margin-top: 1rem;
    padding-left: calc(0.75rem - 0.375rem + 1px);
  }

  /* skeleton loading — mirrors the table columns; shimmer uses theme grays so it adapts to dark mode */
  .list-view-skeleton {
    min-width: $list-view-min-width;
    padding-top: 0.5rem;
    .lv-skeleton-row {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      padding: 0.625rem 0.75rem;
      border-top: 1px solid var(--fts-border);
      &:first-child {
        border-top: 0;
      }
    }
    .lv-skeleton-bar,
    .lv-skeleton-avatar {
      height: 0.75rem;
      border-radius: 0.375rem;
      background: linear-gradient(
        90deg,
        rgba(var(--fts-gray-100-rgb), 0.85) 25%,
        rgba(var(--fts-gray-200-rgb), 0.85) 37%,
        rgba(var(--fts-gray-100-rgb), 0.85) 63%
      );
      background-size: 400% 100%;
      animation: lv-skeleton-shimmer 1.4s ease-in-out infinite;
    }
    .lv-skeleton-avatar {
      width: 1.125rem;
      height: 1.125rem;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .lv-skeleton-bar--id {
      width: 4rem;
      flex-shrink: 0;
    }
    .lv-skeleton-bar--state {
      width: 4.5rem;
      flex-shrink: 0;
    }
    .lv-skeleton-bar--date {
      width: 3.75rem;
      flex-shrink: 0;
    }
    .lv-skeleton-bar--text {
      flex: 1;
      max-width: 22rem;
    }
    .lv-skeleton-row:nth-child(3n) .lv-skeleton-bar--text {
      max-width: 12rem;
    }
    .lv-skeleton-row:nth-child(3n + 1) .lv-skeleton-bar--text {
      max-width: 28rem;
    }
    @media (prefers-reduced-motion: reduce) {
      .lv-skeleton-bar,
      .lv-skeleton-avatar {
        animation: none;
      }
    }
  }

  /* SS05576: freeze the column headers so they stay readable while scrolling a long list.
     Offset by the measured height of .list-header (which is sticky at top: 0 itself).
     Only the listing's own table (.list-view-table) is frozen: a Table column renders a
     nested fts-table-view inside its cell, and freezing that header too pinned it over the
     cell's own rows while scrolling, hiding the links in them (SS06083). The row needs only
     to clear the cells beneath it - a menu that is popped onto <body> gets
     $z-index-popover and stays above it, which $z-index-sticky did not allow, so its
     options could not be clicked once they overlapped the header row (SS06082).

     z-index 2 outranks .list-view-content-header (z-index 1), so any menu still rendered
     in place inside that header is trapped below this row whatever its own z-index -
     pass append-to-body on those (see <fts-sort> above), do not lower this row. */
  .list-view-table > thead > tr > th {
    position: sticky;
    top: var(--fts-list-header-height, 0px);
    z-index: 2;
    background-color: var(--fts-card-background);

    /* border-collapse: collapse detaches the cell borders from the cell, so a sticky th
       leaves its own border behind as it moves - redraw it on the cell. */
    &::after {
      content: "";
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      border-bottom: 1px solid var(--fts-border);
    }
  }

  /* utilities */
  .list-view-mx {
    margin-left: 1rem;
    margin-right: 1rem;
  }
}

@keyframes lv-skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}

.list-nav-title-content.list-nav-title-dropdown-menu {
  min-width: 250px;
  max-height: 500px;
}

/* quick view toggle */
.qv-toggle {
  padding: 0.135rem 1rem;
  font-size: 0.875rem;

  a {
    text-decoration: none;
    color: initial;

    span {
      font-weight: 500;
      color: var(--fts-primary);
    }
  }
}
</style>
